/**
 * How one deploy reads to a person.
 *
 * The enum is the deploy's own vocabulary; this is what somebody scanning a
 * history wants. The distinction that earns its keep is PARTIAL — "12 of 15
 * pages went out" needs a different reaction from either a clean run or a total
 * failure, and a history that renders it as plain success or plain failure will
 * be believed.
 *
 * Pure and dependency-free so jest can reach it.
 */
import type { ReleaseState, ReleaseSummary } from '../../types';

export interface ReleaseBadge {
  label: string;
  className: string;
}

const BADGES: Record<ReleaseState, ReleaseBadge> = {
  RUNNING: {
    label: 'Deploying…',
    className:
      'bg-blue-100 dark:bg-blue-900/40 text-blue-800 dark:text-blue-300 animate-pulse',
  },
  SUCCEEDED: {
    label: 'Deployed',
    className: 'bg-green-100 dark:bg-green-900/40 text-green-800 dark:text-green-300',
  },
  PARTIAL: {
    // Amber, not red: something IS live. Red would send someone looking for a
    // site that never published, when in fact most of it did.
    label: 'Partly deployed',
    className: 'bg-amber-100 dark:bg-amber-900/40 text-amber-900 dark:text-amber-300',
  },
  FAILED: {
    label: 'Failed',
    className: 'bg-red-100 dark:bg-red-900/40 text-red-800 dark:text-red-300',
  },
  EMPTY: {
    label: 'Nothing to send',
    className: 'bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-300',
  },
};

export function releaseBadge(state: ReleaseState | null | undefined): ReleaseBadge {
  if (!state) return BADGES.EMPTY;
  // A state this build has not learned about renders as itself rather than
  // vanishing — an unrecognised deploy state is the one you most want to see.
  return (
    BADGES[state] ?? {
      label: String(state),
      className: 'bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-300',
    }
  );
}

/**
 * The one-line account of what a deploy did.
 *
 * Errors and held pages are only mentioned when there are any: a line that
 * always says "0 errors" trains you to stop reading the number.
 */
export function releaseLine(release: Pick<ReleaseSummary,
  'deployed_count' | 'error_count' | 'held_count'>): string {
  const parts = [
    `${release.deployed_count} page${release.deployed_count === 1 ? '' : 's'} deployed`,
  ];
  if (release.error_count > 0) {
    parts.push(`${release.error_count} failed`);
  }
  if (release.held_count > 0) {
    // Named separately from failures. A held page is not broken — nobody has
    // read it yet — and filing the two together sends someone debugging a page
    // that is simply waiting.
    parts.push(`${release.held_count} held for review`);
  }
  return parts.join(' · ');
}
